"use client";
import { Badge } from "@/src/components/information/badge";
import { Text } from "@/src/components/information/text";
import {
  DataTable,
  createSelectColumn,
  type ColumnDef,
} from "@/src/components/information/data-table";
import { Button } from "@/src/components/input/button";
import { PageContainer } from "@/src/components/layout/page-container";
import { Plus, RefreshCw } from "lucide-react";
import React from "react";
import { useListingController, type Invoice } from "./useListingController";
import { SectionCard } from "@/src/components";

// ─── Helpers ──────────────────────────────────────────────────────

const formatCurrency = (amount: number): string =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(amount);

const formatDate = (dateStr: string): string =>
  new Date(dateStr).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

const getStatusBadgeVariant = (
  status: Invoice["status"]
): "default" | "secondary" | "destructive" | "outline" => {
  switch (status) {
    case "pending":
      return "outline";
    case "approved":
      return "default";
    case "rejected":
      return "destructive";
    case "paid":
      return "secondary";
    default:
      return "outline";
  }
};

// ─── Column Definitions ──────────────────────────────────────────

const columns: ColumnDef<Invoice, any>[] = [
  // Row selection checkbox
  createSelectColumn<Invoice>(),

  // Invoice #
  {
    accessorKey: "number",
    header: "Invoice #",
    cell: ({ row }) => (
      <Text as="span" className="text-primary font-semibold">
        {row.getValue("number")}
      </Text>
    ),
  },

  // Vendor
  {
    accessorKey: "vendor",
    header: "Vendor",
    cell: ({ row }) => <Text as="span">{row.getValue("vendor")}</Text>,
  },

  // Amount (right-aligned)
  {
    accessorKey: "amount",
    header: "Amount",
    cell: ({ row }) => (
      <Text as="span" className="font-medium">
        {formatCurrency(row.getValue("amount"))}
      </Text>
    ),
  },

  // Status (badge)
  {
    accessorKey: "status",
    header: "Status",
    cell: ({ row }) => {
      const status = row.getValue("status") as Invoice["status"];
      return (
        <Badge variant={getStatusBadgeVariant(status)}>
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </Badge>
      );
    },
  },

  // Due Date
  {
    accessorKey: "dueDate",
    header: "Due Date",
    cell: ({ row }) => (
      <Text as="span">{formatDate(row.getValue("dueDate"))}</Text>
    ),
  },

  // Category
  {
    accessorKey: "category",
    header: "Category",
    cell: ({ row }) => <Text as="span">{row.getValue("category")}</Text>,
  },
];

// ─── Page Component ──────────────────────────────────────────────

export default function ListingPage() {
  const ctrl = useListingController();
  const [rowSelection, setRowSelection] = React.useState<
    Record<string, boolean>
  >({});

  const selectedCount = Object.values(rowSelection).filter(Boolean).length;

  const handleBulkAction = (action: string) => {
    const selectedIds = Object.entries(rowSelection)
      .filter(([, selected]) => selected)
      .map(([id]) => id);
    console.log(`Bulk action: ${action} on invoices:`, selectedIds);
    setRowSelection({});
  };

  return (
    <PageContainer size="xl" className="!px-0 !py-0 h-full grid grid-rows-[auto_1fr] overflow-auto">
      {/* Header Section */}
      <div className="py-theme-5 border-b space-y-theme-5 flex justify-between items-start animate-fade-in-up">
        <div>
          <div className="flex items-center gap-theme-3">
            <Text as="h1" className="text-h2 font-bold">
              Invoices
            </Text>
            <Badge variant="default">{ctrl.allInvoices.length} total</Badge>
          </div>
          <Text as="p" className="text-body text-muted-foreground mt-theme-2">
            Manage and track all invoice records
          </Text>
        </div>
        <div className="flex gap-theme-3">
          <Button
            variant="outline"
            size="default"
            leftIcon={<RefreshCw className="w-4 h-4" />}
            onClick={ctrl.refresh}
          >
            Refresh
          </Button>
          <Button
            variant="solid"
            color="primary"
            size="default"
            leftIcon={<Plus className="w-4 h-4" />}
          >
            New Invoice
          </Button>
        </div>
      </div>

      {/* DataTable — handles sorting, pagination, column filters, row selection */}
      <div className="h-full overflow-auto">
        <SectionCard className="!h-full !overflow-auto w-full" contentClassName="h-full overflow-auto">
          <DataTable
            columns={columns}
            data={ctrl.allInvoices}
            filterColumn="number"
            filterPlaceholder="Search by invoice #..."
            showToolbar
            showFooter
            enablePageSize
            rowSelection={rowSelection}
            onRowSelectionChange={setRowSelection}
            getRowId={(row: Invoice) => row.id}
            emptyState={
              <Text as="p" className="text-muted-foreground">
                No invoices found. Try adjusting your filters.
              </Text>
            }
          /></SectionCard>

      </div>
      {/* Bulk Actions Bar */}
      {selectedCount > 0 && (
        <div className="fixed bottom-0 left-0 right-0 border-t border-border bg-background shadow-lg z-40 animate-slide-up">
          <div className="max-w-7xl mx-auto px-theme-6 py-theme-4 flex justify-between items-center">
            <Text as="p" className="text-body font-medium">
              {selectedCount} invoice{selectedCount !== 1 ? "s" : ""} selected
            </Text>
            <div className="flex gap-theme-3">
              <Button
                variant="solid"
                color="primary"
                size="default"
                onClick={() => handleBulkAction("approve")}
              >
                Approve
              </Button>
              <Button
                variant="solid"
                color="destructive"
                size="default"
                onClick={() => handleBulkAction("reject")}
              >
                Reject
              </Button>
              <Button
                variant="outline"
                size="default"
                onClick={() => handleBulkAction("delete")}
              >
                Delete
              </Button>
              <Button
                variant="ghost"
                size="default"
                onClick={() => setRowSelection({})}
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </PageContainer>
  );
}

export { ListingPage };
